import { FullScreenLoading } from '@civilgrid/ui/components';
import * as Toast from '@radix-ui/react-toast';
import {
  createRootRouteWithContext,
  createRoute,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  useSearch,
} from '@tanstack/react-router';
import { useEffect } from 'react';
import { useSnapshot } from 'valtio';
import type { ExportEscalation } from '~/api/internal';
import authStore from '~/authStore';
import type { User } from '~/common/types';
import { NotifyContainer } from '~/components2/Notify';
import TokenValidator from '~/components2/TokenValidator';
import { resolveAuthRedirect } from '~/helpers/resolveAuthRedirect';
import AdminLayout from '~/pages/admin';
import Agencies from '~/pages/admin/civilsearch/agencies';
import AgencyDetails from '~/pages/admin/civilsearch/agencies/agencyDetails';
import CivilSearchLayout from '~/pages/admin/civilsearch/common/CivilSearchLayout';
import CSProjects from '~/pages/admin/civilsearch/projects';
import CSProjectDetails from '~/pages/admin/civilsearch/projects/projectDetails';
import DataRequests from '~/pages/admin/civilsearch/requests';
import RequestDetails from '~/pages/admin/civilsearch/requests/requestDetails';
// Civil Search imports
import CivilSearchers from '~/pages/admin/civilsearch/searchers';
import ProjectUtilityChecks from '~/pages/admin/civilsearch/ssweg';
import SuperAdminLayout from '~/pages/admin/common/SuperAdminLayout';
import ExportEscalations from '~/pages/admin/exportEscalations';
import ExtractTools from '~/pages/admin/extract/ExtractTools';
import CreateOrganization from '~/pages/admin/organization/CreateOrganization';
import CreateSandboxOrg from '~/pages/admin/organization/CreateSandboxOrg';
import JoinOrgAsAdmin from '~/pages/admin/organization/JoinOrgAsAdmin';
import ManageProjectBilling from '~/pages/admin/organization/ManageProjectBilling';
import OrgLayerToggles from '~/pages/admin/organization/OrgLayerToggles';
import OrgSpecialDatasets from '~/pages/admin/organization/OrgSpecialDatasets';
import ResetDemoOrg from '~/pages/admin/organization/ResetDemoOrg';
import TestEnvOverride from '~/pages/admin/organization/TestEnvOverride';
import UpdateLinkedOrganizations from '~/pages/admin/organization/UpdateLinkedOrganizations';
import UpdateOrganizationSubscription from '~/pages/admin/organization/UpdateOrganizationSubscription';
import PgeCloudSync from '~/pages/admin/pge-cloud-sync';
import MoveProject from '~/pages/admin/projects/MoveProject';
import Subscriptions from '~/pages/admin/subscriptions';
import Tools from '~/pages/admin/tools';
// Admin tab imports
import Transactions from '~/pages/admin/transactions';
import UnapprovedUsers from '~/pages/admin/unapprovedUsers';
import Join from '~/pages/auth/Join';
import JoinOrganization from '~/pages/auth/JoinOrganization';
// Page imports — matches current App2.tsx eager imports
import Login from '~/pages/auth/Login';
import Register from '~/pages/auth/Register';
import SSOLogin from '~/pages/auth/SSOLogin';
import BillingSummary from '~/pages/billingSummary';
import Disclaimer from '~/pages/common/Disclaimer';
import ConflictsSummary from '~/pages/conflictsSummary';
import ConflictsSummaryV2 from '~/pages/conflictsSummaryV2';
import Download from '~/pages/Download';
import DataRefreshStatus from '~/pages/dataRefreshStatus';
import DataRequestFulfillment from '~/pages/dataRequestFulfillment';
import Headless from '~/pages/headless';
import HomeMap from '~/pages/homeViewV2';
import LayerLibraryPage from '~/pages/layerLibrary';
import { DEFAULT_LAYER_LIBRARY_TAB } from '~/pages/layerLibrary/helpers';
import Organization from '~/pages/organization';
import OrgSummary from '~/pages/orgSummary';
import ProjectDetails from '~/pages/projectDetails';
import Verification from '~/pages/verification';
import rootStore from '~/store';

// ---------------------------------------------------------------------------
// Router context — fed by valtio stores via RouterProvider
// ---------------------------------------------------------------------------

export type RouterContext = {
  auth: {
    tokenState: 'NOT_LOADED' | 'LOADING' | 'VALID' | 'INVALID';
    isTokenLoading: boolean;
    redirectUri?: string;
  };
  me: User | null;
};

// ---------------------------------------------------------------------------
// Root route
// ---------------------------------------------------------------------------

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: function RootComponent() {
    const { tokenState } = useSnapshot(authStore);
    return (
      <>
        <Outlet />
        {tokenState === 'VALID' && <TokenValidator />}
        <Disclaimer />
        <NotifyContainer />
        <Toast.Viewport
          className="fixed top-top-nav-height z-[999999] m-0 flex w-[400px] max-w-[100vw] flex-col-reverse gap-2 p-4 text-center outline-none"
          style={{
            left: `calc(50% - 200px)`,
          }}
        />
      </>
    );
  },
});

// ---------------------------------------------------------------------------
// Pathless layout routes — auth guards
// ---------------------------------------------------------------------------

const authLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: '_auth',
  beforeLoad: ({ context }) => {
    if (context.auth.tokenState === 'VALID') {
      throw redirect({ to: context.auth.redirectUri || '/home' });
    }
  },
  component: Outlet,
});

const authenticatedLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: '_authenticated',
  beforeLoad: ({ context }) => {
    if (context.auth.tokenState === 'INVALID') {
      throw redirect({ to: '/login' });
    }
  },
  component: function AuthenticatedLayoutComponent() {
    const { me, inUnverifiedMode } = useSnapshot(rootStore);
    const { tokenState } = useSnapshot(authStore);
    const navigate = useNavigate();
    const pathname = useLocation({ select: (l) => l.pathname });

    useEffect(() => {
      if (tokenState === 'INVALID') {
        navigate({ to: '/login' });
      }
    }, [tokenState, navigate]);

    const redirectTo = resolveAuthRedirect(me, inUnverifiedMode, pathname);

    useEffect(() => {
      if (redirectTo) {
        navigate({ to: redirectTo, replace: true });
      }
    }, [redirectTo, navigate]);

    if (!me || redirectTo) return <FullScreenLoading />;
    return <Outlet />;
  },
});

const adminLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: '_admin',
  beforeLoad: ({ context }) => {
    if (context.auth.tokenState === 'INVALID') {
      throw redirect({ to: '/login' });
    }
    // Wait for me to load before checking admin status
    if (context.me && !context.me.isAdmin) {
      throw redirect({ to: '/home' });
    }
  },
  component: function AdminLayoutComponent() {
    const { me } = useSnapshot(rootStore);
    const { tokenState } = useSnapshot(authStore);
    const navigate = useNavigate();

    useEffect(() => {
      if (tokenState === 'INVALID') {
        navigate({ to: '/login' });
      }
    }, [tokenState, navigate]);

    if (!me) return <FullScreenLoading />;
    return <Outlet />;
  },
});

const headlessLayout = createRoute({
  getParentRoute: () => rootRoute,
  id: '_headless',
  beforeLoad: ({ context }) => {
    if (context.auth.tokenState === 'INVALID') {
      throw redirect({ to: '/login' });
    }
  },
  component: function HeadlessLayoutComponent() {
    const { me } = useSnapshot(rootStore);
    const { tokenState } = useSnapshot(authStore);
    const navigate = useNavigate();

    useEffect(() => {
      if (tokenState === 'INVALID') {
        navigate({ to: '/login' });
      }
    }, [tokenState, navigate]);

    if (!me) return <FullScreenLoading />;
    return <Outlet />;
  },
});

// ---------------------------------------------------------------------------
// Auth routes
// ---------------------------------------------------------------------------

const loginRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'login',
  beforeLoad: ({ location }) => {
    const params = new URLSearchParams(location.searchStr);
    const invite = params.get('invite');
    if (invite) {
      throw redirect({ to: '/join', search: { invite } });
    }
  },
  component: Login,
});

const loginAuthCodeRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'login/authCode',
  component: Login,
});

const loginSSORoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'login/sso',
  component: SSOLogin,
});

const registerRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'register',
  component: Register,
});

const joinRoute = createRoute({
  getParentRoute: () => authLayout,
  path: 'join',
  component: Join,
});

// ---------------------------------------------------------------------------
// Protected routes
// ---------------------------------------------------------------------------

const joinOrganizationRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'join-organization',
  component: JoinOrganization,
});

const verificationRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'verification',
  component: Verification,
});

const orgSettingsRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'orgSettings',
  component: Organization,
});

const orgSettingsTabRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'orgSettings/$tabId',
  component: Organization,
});

// Layer Library — CG-509. Index redirects to the default scope tab; the `$tabId` route renders
// the page.
const layerLibraryRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'settings/layerLibrary',
  beforeLoad: () => {
    throw redirect({ to: '/settings/layerLibrary/$tabId', params: { tabId: DEFAULT_LAYER_LIBRARY_TAB } });
  },
});

const layerLibraryTabRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'settings/layerLibrary/$tabId',
  component: LayerLibraryPage,
});

const homeRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'home',
  component: HomeMap,
});

const projectRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'projects/$projectId',
  component: ProjectDetails,
});

const projectTabRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'projects/$projectId/$tabId',
  component: ProjectDetails,
});

const projectTabMapRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'projects/$projectId/$tabId/$mapId',
  component: ProjectDetails,
});

const orgSummaryRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'orgSummary',
  component: OrgSummary,
});

const conflictsSummaryRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'conflictsSummary',
  component: ConflictsSummary,
});

const dataRefreshStatusRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'conflictsSummary/dataRefreshStatus',
  component: DataRefreshStatus,
});

// New Conflict Summary, behind the new-conflict-summary-enabled PostHog flag.
const conflictsSummaryV2Route = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'conflictsSummaryV2',
  component: ConflictsSummaryV2,
});

const billingSummaryRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'billingSummary',
  component: BillingSummary,
});

const dataRequestFulfillmentRoute = createRoute({
  getParentRoute: () => authenticatedLayout,
  path: 'dataRequestFulfillment',
  component: DataRequestFulfillment,
});

// ---------------------------------------------------------------------------
// Admin routes — layout + child routes
// ---------------------------------------------------------------------------

const adminRoute = createRoute({
  getParentRoute: () => adminLayout,
  path: 'admin',
  component: AdminLayout,
});

const adminIndexRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: '/',
  component: () => <>Select a tool on the left panel.</>,
});

function AdminTabPage({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <>
      <div className="my-4 text-base font-medium">{title}</div>
      {children}
    </>
  );
}

const adminTransactionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'transactions',
  component: () => (
    <AdminTabPage title="Transactions">
      <Transactions />
    </AdminTabPage>
  ),
});

const adminSubscriptionsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'subscriptions',
  component: () => (
    <AdminTabPage title="Subscriptions">
      <Subscriptions />
    </AdminTabPage>
  ),
});

const adminCreateOrgRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'create-org',
  component: () => (
    <AdminTabPage title="Create Organization">
      <CreateOrganization />
    </AdminTabPage>
  ),
});

const adminCreateSandboxRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'create-sandbox',
  component: () => (
    <AdminTabPage title="Create Sandbox Org">
      <CreateSandboxOrg />
    </AdminTabPage>
  ),
});

const adminUpdateLinkedOrganizationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'update-linked-organizations',
  component: () => (
    <AdminTabPage title="Update Linked Organizations">
      <UpdateLinkedOrganizations />
    </AdminTabPage>
  ),
});

const adminJoinOrgRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'join-org-as-admin',
  component: () => (
    <AdminTabPage title="Join Org as Admin">
      <JoinOrgAsAdmin />
    </AdminTabPage>
  ),
});

const adminManageBillingRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'manage-project-billing',
  component: () => (
    <AdminTabPage title="Manage Project Billing">
      <ManageProjectBilling />
    </AdminTabPage>
  ),
});

const adminUpdateSubRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'update-organization-subscription',
  component: () => (
    <AdminTabPage title="Update Organization Subscription">
      <UpdateOrganizationSubscription />
    </AdminTabPage>
  ),
});

const adminOrgSpecialDatasetsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'org-special-datasets',
  component: () => (
    <SuperAdminLayout>
      <AdminTabPage title="Org Special Datasets">
        <OrgSpecialDatasets />
      </AdminTabPage>
    </SuperAdminLayout>
  ),
});

const adminExportEscalationsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'export-escalations',
  component: function ExportEscalationsRouteComponent() {
    // No `validateSearch` here on purpose: adding a typed search schema to any route in this file
    // widens the search type `useTableUrlState`'s app-wide, unscoped `navigate({ to: location.pathname })`
    // call is checked against, which then rejects that hook's own (differently-shaped) search state.
    const search = useSearch({ strict: false }) as { tab?: string };
    const tab: ExportEscalation['escalationReason'] = search.tab === 'failed' ? 'failed' : 'restricted';
    const navigate = useNavigate();

    return (
      <SuperAdminLayout>
        <AdminTabPage title="Export Tools">
          <ExportEscalations
            tab={tab}
            onTabChange={(value) =>
              navigate({ to: '/admin/export-escalations', search: { tab: value }, replace: true })
            }
          />
        </AdminTabPage>
      </SuperAdminLayout>
    );
  },
});

const adminLayerTogglesRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'layer-toggles',
  component: () => (
    <SuperAdminLayout>
      <AdminTabPage title="Layer Availability">
        <OrgLayerToggles />
      </AdminTabPage>
    </SuperAdminLayout>
  ),
});

const adminResetDemoOrgRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'reset-demo-org',
  component: () => (
    <SuperAdminLayout>
      <AdminTabPage title="Reset Demo Org">
        <ResetDemoOrg />
      </AdminTabPage>
    </SuperAdminLayout>
  ),
});

const adminTestEnvOverrideRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'test-env-override',
  component: () => (
    <AdminTabPage title="Test Environment Override">
      <TestEnvOverride />
    </AdminTabPage>
  ),
});

const adminToolsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'engineering-tools',
  component: () => (
    <AdminTabPage title="Engineering Tools">
      <Tools />
    </AdminTabPage>
  ),
});

const adminExtractToolsRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'extract-tools',
  component: () => (
    <AdminTabPage title="Extract Tools">
      <ExtractTools />
    </AdminTabPage>
  ),
});

const adminMoveProjectRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'move-projects',
  component: () => (
    <AdminTabPage title="Move Project">
      <MoveProject />
    </AdminTabPage>
  ),
});

const adminPgeCloudSyncRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'pge-cloud-sync',
  component: () => (
    <AdminTabPage title="PG&E Cloud Sync">
      <PgeCloudSync />
    </AdminTabPage>
  ),
});

const adminUnapprovedUsersRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'unapproved-users',
  component: () => (
    <AdminTabPage title="Unapproved Users">
      <UnapprovedUsers />
    </AdminTabPage>
  ),
});

// ---------------------------------------------------------------------------
// Civil Search routes — nested under /admin/civil-search
// ---------------------------------------------------------------------------

const civilSearchRoute = createRoute({
  getParentRoute: () => adminRoute,
  path: 'civil-search',
  component: function CivilSearchRouteComponent() {
    useEffect(() => {
      document.title = 'CivilSearch';
      return () => {
        document.title = 'CivilGrid';
      };
    }, []);
    return <Outlet />;
  },
});

const civilSearchIndexRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: '/',
  component: () => <CivilSearchLayout title="CivilSearch">Select a tool on the left panel.</CivilSearchLayout>,
});

const civilSearchSearchersRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'searchers',
  component: CivilSearchers,
});

const civilSearchRequestsRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'requests',
  component: DataRequests,
});

const civilSearchRequestDetailRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'requests/$requestId',
  component: RequestDetails,
});

const civilSearchRequestDetailTabRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'requests/$requestId/$tab',
  component: RequestDetails,
});

const civilSearchAgenciesRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'agencies',
  component: Agencies,
});

const civilSearchAgencyDetailRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'agencies/$agencyId',
  component: AgencyDetails,
});

const civilSearchAgencyDetailTabRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'agencies/$agencyId/$tab',
  component: AgencyDetails,
});

const civilSearchProjectsRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'projects',
  component: CSProjects,
});

const civilSearchProjectDetailRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'projects/$projectId',
  component: CSProjectDetails,
});

const civilSearchSSWEGRoute = createRoute({
  getParentRoute: () => civilSearchRoute,
  path: 'ssweg',
  component: ProjectUtilityChecks,
});

// ---------------------------------------------------------------------------
// Headless route
// ---------------------------------------------------------------------------

const headlessRoute = createRoute({
  getParentRoute: () => headlessLayout,
  path: 'headless',
  component: Headless,
});

// ---------------------------------------------------------------------------
// Public routes
// ---------------------------------------------------------------------------

const downloadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: 'download',
  component: Download,
});

// ---------------------------------------------------------------------------
// Catch-all → redirect to /home
// ---------------------------------------------------------------------------

const catchAllRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '$',
  beforeLoad: () => {
    throw redirect({ to: '/home' });
  },
  component: () => null,
});

// ---------------------------------------------------------------------------
// Assemble tree
// ---------------------------------------------------------------------------

export const routeTree = rootRoute.addChildren([
  authLayout.addChildren([loginRoute, loginAuthCodeRoute, loginSSORoute, registerRoute, joinRoute]),

  authenticatedLayout.addChildren([
    joinOrganizationRoute,
    verificationRoute,
    orgSettingsRoute,
    orgSettingsTabRoute,
    layerLibraryRoute,
    layerLibraryTabRoute,
    homeRoute,
    projectRoute,
    projectTabRoute,
    projectTabMapRoute,
    orgSummaryRoute,
    conflictsSummaryRoute,
    conflictsSummaryV2Route,
    dataRefreshStatusRoute,
    billingSummaryRoute,
    dataRequestFulfillmentRoute,
  ]),

  adminLayout.addChildren([
    adminRoute.addChildren([
      adminIndexRoute,
      adminTransactionsRoute,
      adminCreateOrgRoute,
      adminCreateSandboxRoute,
      adminUpdateLinkedOrganizationsRoute,
      adminJoinOrgRoute,
      adminManageBillingRoute,
      adminUpdateSubRoute,
      adminOrgSpecialDatasetsRoute,
      adminExportEscalationsRoute,
      adminSubscriptionsRoute,
      adminLayerTogglesRoute,
      adminResetDemoOrgRoute,
      adminTestEnvOverrideRoute,
      adminToolsRoute,
      adminExtractToolsRoute,
      adminMoveProjectRoute,
      adminPgeCloudSyncRoute,
      adminUnapprovedUsersRoute,
      civilSearchRoute.addChildren([
        civilSearchIndexRoute,
        civilSearchSearchersRoute,
        civilSearchRequestsRoute,
        civilSearchRequestDetailRoute,
        civilSearchRequestDetailTabRoute,
        civilSearchAgenciesRoute,
        civilSearchAgencyDetailRoute,
        civilSearchAgencyDetailTabRoute,
        civilSearchProjectsRoute,
        civilSearchProjectDetailRoute,
        civilSearchSSWEGRoute,
      ]),
    ]),
  ]),

  headlessLayout.addChildren([headlessRoute]),

  downloadRoute,
  catchAllRoute,
]);
